/**
 * Whether the signed-in admin may write to a session: reply, add a note,
 * assign, tag, set the priority, close it.
 *
 * ant decides per session, by its system: `chat-message:create` and
 * `chat-session:update` there (the Support Agent role) let the admin write,
 * while the Support Viewer role holds the reads alone. Nothing ant exposes
 * today says which the token holds before a write is tried — no `canWrite`
 * on ChatSession, no permissions on the admin, and the admin's legacy access
 * bits know nothing of the chat — so the console learns it the one way it
 * can: the first write ant refuses with `forbidden` marks the session
 * read-only for the rest of the page's life, the view hides its write
 * controls and says so, and the refusal's toast says why in plain words.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { isForbiddenError } from '../../hooks/chat/client';

/** What the failed write's toast says, after "Could not …". */
export const WRITE_REFUSED_MESSAGE = 'Your role can read this chat but not write to it.';

/** ant refused the write for lack of permission on the session's system. */
export const isWriteRefusal = isForbiddenError;

/** A session write: the session's id first, then whatever the write takes. */
type SessionWrite<A extends unknown[], R> = (sessionId: string, ...rest: A) => Promise<R>;

export interface WriteAccess {
  /** True until ant refuses a write on the session. */
  canWrite: (sessionId: string) => boolean;
  /** Marks the session read-only for this admin, on this page. */
  refuse: (sessionId: string) => void;
  /**
   * Wraps a session write: a refusal marks the session and rejects with
   * WRITE_REFUSED_MESSAGE in place of ant's bare "forbidden"; any other
   * answer passes through untouched.
   */
  guard: <A extends unknown[], R>(write: SessionWrite<A, R>) => SessionWrite<A, R>;
}

/** The sessions ant has refused this admin's writes on, for the page's life. */
export function useWriteAccess(): WriteAccess {
  const [refused, setRefused] = useState<ReadonlySet<string>>(() => new Set());
  // The guards are made once; they read the set through the ref.
  const refusedRef = useRef(refused);
  refusedRef.current = refused;

  const refuse = useCallback((sessionId: string) => {
    if (refusedRef.current.has(sessionId)) return;
    const next = new Set(refusedRef.current);
    next.add(sessionId);
    refusedRef.current = next;
    setRefused(next);
  }, []);

  const guard = useCallback(
    <A extends unknown[], R>(write: SessionWrite<A, R>): SessionWrite<A, R> =>
      async (sessionId, ...rest) => {
        try {
          return await write(sessionId, ...rest);
        } catch (error) {
          if (!isWriteRefusal(error)) throw error;
          refuse(sessionId);
          throw new Error(WRITE_REFUSED_MESSAGE);
        }
      },
    [refuse]
  );

  // Read through the ref: a listener still holding the previous canWrite
  // (a keydown handler between the refusal and its re-render) sees it too.
  // `refused` stays a dependency so the identity changes and views re-render.
  const canWrite = useCallback(
    (sessionId: string) => !refusedRef.current.has(sessionId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refused]
  );

  return useMemo(() => ({ canWrite, refuse, guard }), [canWrite, refuse, guard]);
}
